import {useMutation, UseMutationResult, useQuery, UseQueryResult} from '@tanstack/react-query';
import {useCallback, useEffect} from 'react';

import {useLocale} from '../../provider/locale/locale-hook';
import {useSnackbar} from '../../provider/snackbar/snackbar-hook';
import {catchError} from '../../util/promise';
import {useApiHooks} from '../api-hook/api-hook';
import {UseHookType} from '../api-hook/api-hook-type';

import {
    disconnectTelegramAccount,
    fetchBrowserNotifications,
    fetchEmailNotifications,
    fetchInvitationLink,
    fetchTelegramAccounts,
    fetchTelegramAnalyticsSettings,
    fetchTelegramDirectLink,
    fetchTelegramNotifications,
    fetchUsedeskCatalogList,
    getUpdateTelegramAnalyticsSettingsUrl,
    telegramNotificationsUrl,
    updateBrowserNotifications as fetchUpdateBrowserNotifications,
    updateEmailNotifications as fetchUpdateEmailNotifications,
    updateTelegramAnalyticsSettings,
    updateTelegramNotifications as fetchUpdateTelegramNotifications,
} from './notifications-settings-api';
import {
    BrowserNotificationsType,
    EmailNotificationsType,
    TelegramAccountsType,
    TelegramAnalyticsSettingsType,
    TelegramLinkType,
    TelegramNotificationsType,
    UpdateTelegramAnalyticsSettingsType,
    UsedeskCatalogResponseType,
} from './notifications-settings-type';

type UpdateEmailNotificationsAdditionalType = {
    updateEmailNotifications: (settings: EmailNotificationsType) => Promise<EmailNotificationsType | void>;
};

type UpdateBrowserNotificationsAdditionalType = {
    updateBrowserNotifications: (
        settings: BrowserNotificationsType
    ) => Promise<BrowserNotificationsType | void | Error>;
};

type UpdateTelegramNotificationsAdditionalType = {
    updateTelegramNotifications: (
        settings: TelegramNotificationsType
    ) => Promise<TelegramNotificationsType | void | Error>;
};

export function useEmailNotifications(): UseHookType<EmailNotificationsType> & UpdateEmailNotificationsAdditionalType {
    const {isInProgress, setIsInProgress, processError, setProcessError, result, setResult, reset} =
        useApiHooks<EmailNotificationsType>();

    useEffect(() => {
        setIsInProgress(true);

        fetchEmailNotifications()
            .then(setResult)
            .finally(() => setIsInProgress(false))
            .catch(setProcessError);
    }, [setIsInProgress, setProcessError, setResult]);

    const updateEmailNotifications = useCallback(
        (settings: EmailNotificationsType): Promise<EmailNotificationsType | void> => {
            setIsInProgress(true);

            return fetchUpdateEmailNotifications(settings)
                .then(setResult)
                .finally(() => setIsInProgress(false))
                .catch(setProcessError);
        },
        [setIsInProgress, setProcessError, setResult]
    );

    return {isInProgress, processError, result, reset, updateEmailNotifications};
}

export function useBrowserNotifications(): UseHookType<BrowserNotificationsType> &
    UpdateBrowserNotificationsAdditionalType {
    const {isInProgress, setIsInProgress, processError, setProcessError, result, setResult, reset} =
        useApiHooks<BrowserNotificationsType>();

    useEffect(() => {
        setIsInProgress(true);

        fetchBrowserNotifications()
            .then(setResult)
            .finally(() => setIsInProgress(false))
            .catch(setProcessError);
    }, [setIsInProgress, setProcessError, setResult]);

    const updateBrowserNotifications = useCallback(
        (settings: BrowserNotificationsType): Promise<BrowserNotificationsType | void | Error> => {
            setIsInProgress(true);

            return fetchUpdateBrowserNotifications(settings)
                .then(setResult)
                .finally(() => setIsInProgress(false))
                .catch(catchError);
        },
        [setIsInProgress, setResult]
    );

    return {isInProgress, processError, result, reset, updateBrowserNotifications};
}

export function useTelegramNotifications(): UseHookType<TelegramNotificationsType> &
    UpdateTelegramNotificationsAdditionalType {
    const {isInProgress, setIsInProgress, processError, setProcessError, result, setResult, reset} =
        useApiHooks<TelegramNotificationsType>();

    useEffect(() => {
        setIsInProgress(true);

        fetchTelegramNotifications()
            .then(setResult)
            .finally(() => setIsInProgress(false))
            .catch(setProcessError);
    }, [setIsInProgress, setProcessError, setResult]);

    const updateTelegramNotifications = useCallback(
        (settings: TelegramNotificationsType): Promise<TelegramNotificationsType | void | Error> => {
            setIsInProgress(true);

            return fetchUpdateTelegramNotifications(settings)
                .then(setResult)
                .finally(() => setIsInProgress(false))
                .catch(catchError);
        },
        [setIsInProgress, setResult]
    );

    return {isInProgress, processError, result, reset, updateTelegramNotifications};
}

export function useUsedeskCatalogList(): UseHookType<UsedeskCatalogResponseType> {
    const {isInProgress, setIsInProgress, processError, setProcessError, result, setResult, reset} =
        useApiHooks<UsedeskCatalogResponseType>();

    useEffect(() => {
        setIsInProgress(true);
        setProcessError(null);

        fetchUsedeskCatalogList()
            .then(setResult)
            .finally(() => setIsInProgress(false))
            .catch(setProcessError);
    }, [setIsInProgress, setProcessError, setResult]);

    return {isInProgress, processError, result, reset};
}

export function useInvitationLink(): UseQueryResult<TelegramLinkType> {
    return useQuery(['invitation-link'], () => fetchInvitationLink());
}

export function useTelegramAccounts(): UseQueryResult<TelegramAccountsType> {
    return useQuery([`${telegramNotificationsUrl}/user_tg_accounts/`], () => fetchTelegramAccounts(), {
        refetchOnWindowFocus: true,
    });
}

export function useTelegramAnalyticsSettings(): UseQueryResult<TelegramAnalyticsSettingsType> {
    return useQuery(['telegram-analytics-settings'], () => fetchTelegramAnalyticsSettings(), {
        refetchOnWindowFocus: true,
        refetchInterval(query) {
            return !query ? 5000 : false;
        },
    });
}

export function useUpdateTelegramAnalyticsSettings(
    settingsId: string
): UseMutationResult<TelegramAnalyticsSettingsType, unknown, UpdateTelegramAnalyticsSettingsType, unknown> {
    const {snackbar} = useSnackbar();
    const {getLocalizedString} = useLocale();

    return useMutation(
        [getUpdateTelegramAnalyticsSettingsUrl(settingsId)],
        (values: UpdateTelegramAnalyticsSettingsType) => updateTelegramAnalyticsSettings(settingsId, values),
        {
            onSuccess: () => {
                snackbar.success({
                    description: getLocalizedString('NOTIFICATIONS__TAB_INFO__TELEGRAM_NOTIFICATIONS__UPDATED'),
                    message: getLocalizedString('NOTIFICATIONS__SETTINGS_SAVED'),
                });
            },
        }
    );
}

export function useTelegramDirectLink(): UseQueryResult<TelegramLinkType> {
    return useQuery(['direct-link'], () => fetchTelegramDirectLink());
}

export function useDisconnectTelegramAccount(): UseMutationResult<void, unknown, string> {
    return useMutation({
        mutationKey: [`${telegramNotificationsUrl}/inactivate_tg_account/`],
        mutationFn: (id) => disconnectTelegramAccount(id),
    });
}
